import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit2, Trash2, XCircle, Star, Phone, MapPin, Briefcase, Shield, RefreshCw, ChevronLeft, ChevronRight, Eye, EyeOff } from 'lucide-react';
import {
  getProfessionals, createProfessional, updateProfessional,
  deleteProfessional, toggleProfessionalStatus, toggleVerification,
  CATEGORIES, STATUSES, DAYS
} from '../../../services/professionalService';

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-gray-100 text-gray-600',
    pending: 'bg-yellow-100 text-yellow-800',
    suspended: 'bg-red-100 text-red-800',
  };
  const labels = { active: 'Actif', inactive: 'Inactif', pending: 'En attente', suspended: 'Suspendu' };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${map?.[status] || 'bg-gray-100 text-gray-600'}`}>
      {labels?.[status] || status}
    </span>
  );
};

// ─── Professional Form Modal ──────────────────────────────────────────────────
const ProfessionalFormModal = ({ professional, onClose, onSave }) => {
  const isEdit = !!professional?.id;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    fullName: professional?.fullName || '',
    email: professional?.email || '',
    phone: professional?.phone || '',
    whatsapp: professional?.whatsapp || '',
    category: professional?.category || 'autre',
    specialty: professional?.specialty || '',
    bio: professional?.bio || '',
    yearsExperience: professional?.yearsExperience || 0,
    city: professional?.city || 'Abidjan',
    commune: professional?.commune || '',
    address: professional?.address || '',
    serviceZones: professional?.serviceZones?.join(', ') || '',
    status: professional?.status || 'pending',
    isVerified: professional?.isVerified || false,
    isFeatured: professional?.isFeatured || false,
    hourlyRate: professional?.hourlyRate || '',
    dailyRate: professional?.dailyRate || '',
    currency: professional?.currency || 'XOF',
    availableDays: professional?.availableDays || [],
    availableHoursStart: professional?.availableHoursStart || '',
    availableHoursEnd: professional?.availableHoursEnd || '',
    responseTimeHours: professional?.responseTimeHours || 24,
    websiteUrl: professional?.websiteUrl || '',
    linkedinUrl: professional?.linkedinUrl || '',
    tags: professional?.tags?.join(', ') || '',
    certifications: professional?.certifications?.join(', ') || '',
    languages: professional?.languages?.join(', ') || 'Français',
    notes: professional?.notes || '',
    avatarUrl: professional?.avatarUrl || '',
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const toggleDay = (day) => {
    setForm(prev => ({
      ...prev,
      availableDays: prev?.availableDays?.includes(day)
        ? prev?.availableDays?.filter(d => d !== day)
        : [...prev?.availableDays, day]
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.fullName?.trim()) { setError('Le nom complet est requis.'); return; }
    if (!form?.category) { setError('La catégorie est requise.'); return; }

    setSaving(true);
    setError(null);

    const payload = {
      ...form,
      serviceZones: form?.serviceZones ? form?.serviceZones?.split(',')?.map(s => s?.trim())?.filter(Boolean) : [],
      tags: form?.tags ? form?.tags?.split(',')?.map(s => s?.trim())?.filter(Boolean) : [],
      certifications: form?.certifications ? form?.certifications?.split(',')?.map(s => s?.trim())?.filter(Boolean) : [],
      languages: form?.languages ? form?.languages?.split(',')?.map(s => s?.trim())?.filter(Boolean) : ['Français'],
    };

    const result = isEdit
      ? await updateProfessional(professional?.id, payload)
      : await createProfessional(payload);

    setSaving(false);
    if (result?.error) { setError(result?.error); return; }
    onSave(result?.data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? 'Modifier le Professionnel' : 'Ajouter un Professionnel'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}

          {/* Identity */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Informations Personnelles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet *</label>
                <input name="fullName" value={form?.fullName} onChange={handleChange} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Kouassi Jean-Baptiste" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input name="email" type="email" value={form?.email} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="email@exemple.ci" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input name="phone" value={form?.phone} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+225 07 00 00 00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                <input name="whatsapp" value={form?.whatsapp} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+225 07 00 00 00" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Photo (URL)</label>
                <input name="avatarUrl" value={form?.avatarUrl} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://..." />
              </div>
            </div>
          </div>

          {/* Professional Info */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Informations Professionnelles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                <select name="category" value={form?.category} onChange={handleChange} required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {CATEGORIES?.map(c => <option key={c?.value} value={c?.value}>{c?.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Spécialité</label>
                <input name="specialty" value={form?.specialty} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Architecture résidentielle" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Années d'expérience</label>
                <input name="yearsExperience" type="number" min="0" max="60" value={form?.yearsExperience} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select name="status" value={form?.status} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {STATUSES?.map(s => <option key={s?.value} value={s?.value}>{s?.label}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Biographie</label>
                <textarea name="bio" value={form?.bio} onChange={handleChange} rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Décrivez le professionnel..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags (séparés par virgule)</label>
                <input name="tags" value={form?.tags} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Rénovation, Plans 3D, ..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Certifications (séparées par virgule)</label>
                <input name="certifications" value={form?.certifications} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ISO 9001, CAP Électricité, ..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Langues (séparées par virgule)</label>
                <input name="languages" value={form?.languages} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Français, Anglais, ..." />
              </div>
            </div>
          </div>

          {/* Location */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Localisation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                <input name="city" value={form?.city} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Abidjan" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Commune</label>
                <input name="commune" value={form?.commune} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Cocody, Plateau, ..." />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <input name="address" value={form?.address} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Adresse complète" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Zones d'intervention (séparées par virgule)</label>
                <input name="serviceZones" value={form?.serviceZones} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Cocody, Plateau, Marcory, ..." />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Tarification</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tarif horaire</label>
                <input name="hourlyRate" type="number" min="0" value={form?.hourlyRate} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="25000" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tarif journalier</label>
                <input name="dailyRate" type="number" min="0" value={form?.dailyRate} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="150000" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Devise</label>
                <select name="currency" value={form?.currency} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="XOF">XOF (FCFA)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="USD">USD ($)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Availability */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Disponibilité</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">Jours disponibles</label>
              <div className="flex flex-wrap gap-2">
                {DAYS?.map(day => (
                  <button key={day?.value} type="button" onClick={() => toggleDay(day?.value)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      form?.availableDays?.includes(day?.value)
                        ? 'bg-blue-600 text-white border-blue-600' :'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                    }`}>
                    {day?.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Heure début</label>
                <input name="availableHoursStart" type="time" value={form?.availableHoursStart} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Heure fin</label>
                <input name="availableHoursEnd" type="time" value={form?.availableHoursEnd} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Délai réponse (heures)</label>
                <input name="responseTimeHours" type="number" min="1" max="168" value={form?.responseTimeHours} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          </div>

          {/* Links */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Liens & Réseaux</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site web</label>
                <input name="websiteUrl" value={form?.websiteUrl} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
                <input name="linkedinUrl" value={form?.linkedinUrl} onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://linkedin.com/in/..." />
              </div>
            </div>
          </div>

          {/* Flags */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Options</h3>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="isVerified" checked={form?.isVerified} onChange={handleChange}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                <span className="text-sm text-gray-700">Profil vérifié</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="isFeatured" checked={form?.isFeatured} onChange={handleChange}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                <span className="text-sm text-gray-700">Mis en avant</span>
              </label>
            </div>
          </div>

          {/* Admin Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes internes (admin)</label>
            <textarea name="notes" value={form?.notes} onChange={handleChange} rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Notes visibles uniquement par les admins..." />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <button type="button" onClick={onClose}
              className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
              Annuler
            </button>
            <button type="submit" disabled={saving}
              className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2">
              {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Enregistrer les modifications' : 'Ajouter le professionnel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ProfessionalManagement = () => {
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [showModal, setShowModal] = useState(false);
  const [editingProfessional, setEditingProfessional] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data, count, error } = await getProfessionals({
      category: filterCategory,
      status: filterStatus,
      search,
      page,
      pageSize,
    });
    setLoading(false);
    if (error) { showToast(error, 'error'); return; }
    setProfessionals(data);
    setTotal(count);
  }, [filterCategory, filterStatus, search, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = (saved) => {
    setShowModal(false);
    setEditingProfessional(null);
    showToast(editingProfessional?.id ? 'Professionnel mis à jour.' : 'Professionnel ajouté.');
    fetchData();
  };

  const handleDelete = async (pro) => {
    if (!window.confirm(`Supprimer "${pro?.fullName}" ? Cette action est irréversible.`)) return;
    setActionLoading(pro?.id + '-delete');
    const { error } = await deleteProfessional(pro?.id);
    setActionLoading(null);
    if (error) { showToast(error, 'error'); return; }
    showToast('Professionnel supprimé.');
    fetchData();
  };

  const handleToggleStatus = async (pro) => {
    const newStatus = pro?.status === 'active' ? 'inactive' : 'active';
    setActionLoading(pro?.id + '-status');
    const { error } = await toggleProfessionalStatus(pro?.id, newStatus);
    setActionLoading(null);
    if (error) { showToast(error, 'error'); return; }
    showToast(`Statut changé en "${newStatus === 'active' ? 'Actif' : 'Inactif'}".`);
    fetchData();
  };

  const handleToggleVerify = async (pro) => {
    setActionLoading(pro?.id + '-verify');
    const { error } = await toggleVerification(pro?.id, !pro?.isVerified);
    setActionLoading(null);
    if (error) { showToast(error, 'error'); return; }
    showToast(pro?.isVerified ? 'Vérification retirée.' : 'Profil vérifié.');
    fetchData();
  };

  const totalPages = Math.ceil(total / pageSize);
  const categoryLabel = (val) => CATEGORIES?.find(c => c?.value === val)?.label || val;

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition-all ${
          toast?.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        }`}>
          {toast?.message}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Gestion des Professionnels</h3>
          <p className="text-sm text-gray-500 mt-0.5">{total} professionnel{total !== 1 ? 's' : ''} au total</p>
        </div>
        <button
          onClick={() => { setEditingProfessional(null); setShowModal(true); }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Ajouter un professionnel
        </button>
      </div>
      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-48 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={e => { setSearch(e?.target?.value); setPage(1); }}
              placeholder="Rechercher par nom, spécialité, commune..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filterCategory}
            onChange={e => { setFilterCategory(e?.target?.value); setPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Toutes catégories</option>
            {CATEGORIES?.map(c => <option key={c?.value} value={c?.value}>{c?.label}</option>)}
          </select>
          <select
            value={filterStatus}
            onChange={e => { setFilterStatus(e?.target?.value); setPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tous statuts</option>
            {STATUSES?.map(s => <option key={s?.value} value={s?.value}>{s?.label}</option>)}
          </select>
          <button onClick={fetchData} className="flex items-center gap-1 border border-gray-300 text-gray-600 px-3 py-2 rounded-lg text-sm hover:bg-gray-50 transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : professionals?.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">Aucun professionnel trouvé</p>
            <p className="text-sm mt-1">Ajoutez votre premier professionnel ou modifiez les filtres.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Professionnel</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Catégorie</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Localisation</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Tarifs</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Note</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Statut</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {professionals?.map(pro => (
                  <tr key={pro?.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {pro?.avatarUrl
                            ? <img src={pro?.avatarUrl} alt={pro?.fullName} className="w-full h-full object-cover" />
                            : <span className="text-blue-700 font-bold text-sm">{pro?.fullName?.charAt(0)?.toUpperCase()}</span>
                          }
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-gray-900">{pro?.fullName}</span>
                            {pro?.isVerified && <Shield className="w-3.5 h-3.5 text-blue-600" title="Vérifié" />}
                            {pro?.isFeatured && <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" title="Mis en avant" />}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">{pro?.specialty || '—'}</div>
                          {pro?.phone && (
                            <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                              <Phone className="w-3 h-3" />{pro?.phone}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded text-xs font-medium">
                        {categoryLabel(pro?.category)}
                      </span>
                      <div className="text-xs text-gray-400 mt-1">{pro?.yearsExperience} ans exp.</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-gray-700 text-xs">
                        <MapPin className="w-3 h-3 text-gray-400" />
                        {pro?.commune ? `${pro?.commune}, ` : ''}{pro?.city}
                      </div>
                      {pro?.serviceZones?.length > 0 && (
                        <div className="text-xs text-gray-400 mt-0.5">{pro?.serviceZones?.slice(0, 2)?.join(', ')}{pro?.serviceZones?.length > 2 ? ` +${pro?.serviceZones?.length - 2}` : ''}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {pro?.hourlyRate ? <div>{Number(pro?.hourlyRate)?.toLocaleString()} {pro?.currency}/h</div> : null}
                      {pro?.dailyRate ? <div>{Number(pro?.dailyRate)?.toLocaleString()} {pro?.currency}/j</div> : null}
                      {!pro?.hourlyRate && !pro?.dailyRate && <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                        <span className="font-medium text-gray-800">{Number(pro?.ratingAverage || 0)?.toFixed(1)}</span>
                        <span className="text-gray-400 text-xs">({pro?.ratingCount || 0})</span>
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">{pro?.completedJobs || 0} missions</div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={pro?.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {/* Verify toggle */}
                        <button
                          onClick={() => handleToggleVerify(pro)}
                          disabled={actionLoading === pro?.id + '-verify'}
                          title={pro?.isVerified ? 'Retirer la vérification' : 'Vérifier'}
                          className={`p-1.5 rounded transition-colors ${pro?.isVerified ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-400 hover:bg-gray-100'}`}
                        >
                          {actionLoading === pro?.id + '-verify'
                            ? <RefreshCw className="w-4 h-4 animate-spin" />
                            : <Shield className="w-4 h-4" />
                          }
                        </button>
                        {/* Status toggle */}
                        <button
                          onClick={() => handleToggleStatus(pro)}
                          disabled={actionLoading === pro?.id + '-status'}
                          title={pro?.status === 'active' ? 'Désactiver' : 'Activer'}
                          className={`p-1.5 rounded transition-colors ${pro?.status === 'active' ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                        >
                          {actionLoading === pro?.id + '-status'
                            ? <RefreshCw className="w-4 h-4 animate-spin" />
                            : pro?.status === 'active' ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />
                          }
                        </button>
                        {/* Edit */}
                        <button
                          onClick={() => { setEditingProfessional(pro); setShowModal(true); }}
                          title="Modifier"
                          className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(pro)}
                          disabled={actionLoading === pro?.id + '-delete'}
                          title="Supprimer"
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                        >
                          {actionLoading === pro?.id + '-delete'
                            ? <RefreshCw className="w-4 h-4 animate-spin" />
                            : <Trash2 className="w-4 h-4" />
                          }
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-sm text-gray-600">
              Page {page} sur {totalPages} — {total} résultats
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-600 hover:bg-white disabled:opacity-40 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" /> Précédent
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-600 hover:bg-white disabled:opacity-40 transition-colors"
              >
                Suivant <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Modal */}
      {showModal && (
        <ProfessionalFormModal
          professional={editingProfessional}
          onClose={() => { setShowModal(false); setEditingProfessional(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default ProfessionalManagement;
